// types/chatStream.ts
//
// The Ora chat turn stream protocol (ora-be ADR-0006), as the frontend sees
// it. This is a cross-app contract with `ora-be`'s v5 chat turn route: every
// event carries `turnId` and a monotonic `seq`, `turn.done` and `error` are
// terminal, and adding an event is additive while changing a payload shape
// bumps `turn.start.protocol`.
//
// Nothing here knows about Redux or `fetch`; the stream client parses onto
// these types and the chat slice reduces them.

import type { Region } from './documentModel';

/** The protocol version this client is written against (`turn.start.protocol`). */
export const CHAT_STREAM_PROTOCOL_VERSION = 1;

/** The v5 route the turn request is sent to. */
export const CHAT_TURN_ROUTE = '/api/v5/chat/turn';

/** Every event on the wire carries these. */
export interface ChatStreamEventEnvelope {
  turnId: string;
  seq: number;
}

/** Remaining daily chat allowance, as reported on `turn.start` and `turn.done`. */
export interface ChatDailyLimits {
  /** Chats the user has left today, or null when the server does not cap them. */
  chatsRemaining: number | null;
  /** The user's daily cap, or null when uncapped. */
  chatsLimit: number | null;
}

/** Where a block hangs relative to the assistant text. */
export type ChatBlockPlacement = 'inline' | 'end';

/** A route plus ids the client can navigate to. Never a raw path from the model. */
export interface ChatNavigationTarget {
  route: string;
  params?: Record<string, string>;
}

/**
 * Citation kinds for the student's own data (ORA-514). Emitted by the
 * performance, study-status and memory-read tools when they ran this turn —
 * one per kind per turn — with an empty `contentId`, since they name no
 * content row. They are sources a student can see, never blocks they can add.
 */
export type ChatOwnDataCitationKind = 'performance' | 'study_plan' | 'memory';

/** A structured citation on `text.done`. No inline `[A1]`/`[pN]` shorthand exists. */
export interface ChatCitation {
  /** Stable id for this citation within the turn. */
  id: string;
  /**
   * Content kind, e.g. `article`, `video`, `question`, `flashcard`,
   * `document_page`, `help_doc`, or one of `ChatOwnDataCitationKind`.
   */
  kind: string;
  /** Id of the cited content in its own domain. Empty for own-data kinds. */
  contentId: string;
  title?: string;
  sectionId?: string | null;
  page?: number | null;
  /** The lecture a `document_page` citation belongs to. */
  lectureId?: string;
  /** The attachment a `chat_document_page` citation belongs to. */
  documentId?: string;
  /** Optional bounded source text for a `document_page` citation. */
  excerpt?: string;
  /**
   * A `document_page` citation into a lecture's document model (Document
   * Pipeline v4): the bare fact keys it cites and their boxes on the page,
   * which the viewer highlights. One deep-link shape for summary page chips
   * and, later, mapped items and transcript turns. Absent on older citations,
   * which open the page without highlights.
   */
  factIds?: string[];
  regions?: Region[];
  /**
   * Student-facing "Kind · short name", built server-side (ORA-514), e.g.
   * "Article · Nephrotic syndrome" or "Cardiology notes · p. 12"; own-data
   * kinds carry "Performance data", "Study plan", "AI Memory".
   * The client falls back to a kind label when it is absent.
   */
  label?: string;
  /** True when the same item was emitted as a `block` this turn; hidden from Sources. */
  rendered?: boolean;
  /**
   * True when the plus may add it as a block through the add-as-block route:
   * question, flashcard, article, video, help_doc, document_page. Absent or
   * false for own-data kinds.
   */
  addable?: boolean;
  /**
   * Where opening this source takes the student (ORA-605), as a named route
   * the client resolves itself. Written server-side for the kinds with an
   * honest destination; absent on the rest, and absent on citations written
   * before it existed — which the client still resolves the old way.
   */
  target?: ChatNavigationTarget;
  /**
   * mcgraw-hill (client only, never on the wire): opening this source
   * highlights its `excerpt` on the page.
   */
  highlightExcerpt?: boolean;
}

/**
 * What a finished turn did, as the conversation read returns it (ORA-604):
 * the status labels the student watched go by, and how long the turn took.
 * Persisted on the turn's trace, so an answer read back from history can show
 * the same "Thought for 4.0s · 3 steps" line it showed live.
 *
 * `version` is the record's own shape, not the stream protocol's: a version
 * this client does not know is skipped rather than guessed at.
 */
export interface ChatMessageActivity {
  version: 1;
  statusTrail: string[];
  ms: number;
}

/**
 * A vetted, immutable label for the study item a student asked Ora about.
 * The server derives it from its turn snapshot; it is never the client
 * envelope, an item id, or raw question/card content.
 */
export type ChatContextLabel = {
  kind: 'question' | 'flashcard' | 'lecture' | 'video' | 'article' | 'exam';
  label: string;
  /** Present for lecture context when the server or pending envelope has it. */
  lectureId?: string;
};

/** A follow-up chip: either a prompt to send, or somewhere to navigate. */
export type ChatFollowup =
  | {
      id: string;
      kind: 'prompt';
      label: string;
      prompt: string;
      /**
       * What the chip is asking for, when the server says (ORA-827). A `study`
       * chip asks for practice rather than for another explanation, and is drawn
       * with its own glyph; it is sent exactly like any other prompt chip.
       *
       * Optional on purpose: the server shipped chips without it for a long time
       * and still does for question chips, so its absence is normal rather than
       * a chip that failed to parse.
       */
      intent?: 'study';
    }
  | {
      id: string;
      kind: 'navigate';
      label: string;
      target: ChatNavigationTarget;
    };

export interface ChatTurnStartEvent extends ChatStreamEventEnvelope {
  type: 'turn.start';
  protocol: number;
  conversationId: string;
  model: string;
  limits: ChatDailyLimits | null;
}

export interface ChatStatusEvent extends ChatStreamEventEnvelope {
  type: 'status';
  /** Short human label, e.g. "Searching questions". Replaces the legacy `stage`. */
  label: string;
}

export interface ChatToolCallEvent extends ChatStreamEventEnvelope {
  type: 'tool.call';
  callId: string;
  name: string;
  /** Sanitized args; never raw user content. */
  args: Record<string, unknown>;
}

export interface ChatToolResultEvent extends ChatStreamEventEnvelope {
  type: 'tool.result';
  callId: string;
  ok: boolean;
  ms: number;
  /** A summary line — never the raw tool payload. */
  summary: string;
}

export interface ChatBlockEvent extends ChatStreamEventEnvelope {
  type: 'block';
  blockId: string;
  kind: string;
  placement: ChatBlockPlacement;
  payload: Record<string, unknown>;
  /** Where "Open" takes the user, when the block has a full-app home. */
  target?: ChatNavigationTarget | null;
}

export interface ChatTextDeltaEvent extends ChatStreamEventEnvelope {
  type: 'text.delta';
  delta: string;
}

export interface ChatTextDoneEvent extends ChatStreamEventEnvelope {
  type: 'text.done';
  /** The authoritative full text; replaces whatever the deltas built. */
  text: string;
  citations: ChatCitation[];
  /** Safe context snapshot for the associated user message, when anchored. */
  contextLabel?: ChatContextLabel;
}

export interface ChatFollowupsEvent extends ChatStreamEventEnvelope {
  type: 'followups';
  items: ChatFollowup[];
}

/**
 * One row of the action card's detail list, rendered by the server so the card
 * does not have to humanise raw args (ORA-607). Optional: an older backend
 * sends none and the card falls back to labelling `args` itself.
 */
export interface ChatActionDetail {
  label: string;
  value: string;
}

export interface ChatActionProposedEvent extends ChatStreamEventEnvelope {
  type: 'action.proposed';
  actionId: string;
  name: string;
  args: Record<string, unknown>;
  /** Human summary of what confirming would do. */
  summary: string;
  /** Server-rendered detail rows; preferred over `args` when present. */
  details?: ChatActionDetail[];
  /** What the confirm button should say, when the server has an opinion. */
  confirmLabel?: string;
}

/**
 * How a proposal ended once the server settled it itself (ORA-743). `FAILED` is
 * distinct from `EXPIRED`: an action confirmed in words can fail exactly as one
 * confirmed by a tap can, and the card answers the two differently — a failure
 * is worth offering a retry for, a dead proposal is not.
 */
export type ChatActionResolvedStatus =
  'EXECUTED' | 'FAILED' | 'DISMISSED' | 'EXPIRED';

/** The public part of an executed action's 24-hour Undo contract. */
export interface ChatActionUndo {
  available: boolean;
  expiresAt: string;
  /** Today-target restores can redraw the session's concrete items. */
  approximate?: boolean;
  /** Server-authored explanation for an approximate restore. */
  note?: string;
  /** Present after an Undo succeeded; private snapshots never cross the wire. */
  undoneAt?: string;
}

/**
 * An action's own result — what the client must act on rather than navigate to.
 * Declared structurally rather than imported from `services/chatActions`
 * (this module is the wire contract and imports nothing), and deliberately the
 * same shape that endpoint returns, so an auto-confirmed action reaches the card
 * exactly as a tapped one does.
 *
 * `support_sent` is `contact_support` since ORA-743: the message has already
 * reached Intercom, so there is nothing to open and nothing to retry — its
 * `conversationId` and `url` are operator-only and are never rendered.
 * `open_support` is the pre-ORA-743 shape, kept so this client still works
 * against a backend that has not shipped the server-side send.
 */
export type ChatActionResolvedResult =
  | {
      kind: 'support_sent';
      topic: string | null;
      conversationId: string;
      url?: string;
    }
  | { kind: 'open_support'; topic: string | null; prefill: string }
  | {
      kind: 'plan_change' | 'study_change';
      undo?: ChatActionUndo;
      [key: string]: unknown;
    };

/**
 * The server settled a proposal without the card being tapped (ORA-743): the
 * student answered "yes" / "send it" / "never mind" in words, and the runtime
 * confirmed and ran it, or recorded the dismissal, or found it already gone.
 *
 * Emitted early in the turn AFTER the one that proposed the action, so the card
 * it changes always belongs to an earlier turn. Note that the turn it names is
 * `proposedTurnId`, NOT the envelope's `turnId`: the emitter owns `turnId` on
 * every event and stamps it with the turn being streamed, which would overwrite
 * a body field of the same name. `proposedTurnId` is a SERVER turn id, so the
 * client matches it against `serverTurnId` — and only as a hint, because
 * `actionId` is what actually identifies the card (see `chatSlice`).
 */
export interface ChatActionResolvedEvent extends ChatStreamEventEnvelope {
  type: 'action.resolved';
  actionId: string;
  /**
   * The turn that proposed the card. Optional here though the route always
   * sends it: without it the client still finds the card by `actionId`, and
   * rejecting the event would cost the reader its seq cursor for nothing.
   */
  proposedTurnId?: string;
  name: string;
  status: ChatActionResolvedStatus;
  /** The action's own result. Present only on `EXECUTED`. */
  result?: ChatActionResolvedResult | null;
  /** Where the student can go to use what ran, when there is somewhere. */
  target?: ChatNavigationTarget | null;
  /** One sentence describing what ran, as the card would have shown it. */
  summary?: string;
  /** A student-facing sentence for a `FAILED` outcome. Never a raw error. */
  error?: string;
}

export interface ChatTitleEvent extends ChatStreamEventEnvelope {
  type: 'title';
  title: string;
}

export interface ChatTurnDoneEvent extends ChatStreamEventEnvelope {
  type: 'turn.done';
  traceId: string;
  steps: number;
  ms: number;
  limits: ChatDailyLimits | null;
  /**
   * Server id of the assistant message this turn produced, when the runtime
   * supplies it. Optional so an older backend still parses; without it a
   * regenerate of a just-streamed message has only the client-side id.
   */
  messageId?: string | null;
}

export interface ChatErrorEvent extends ChatStreamEventEnvelope {
  type: 'error';
  code: string;
  retryable: boolean;
  message?: string;
  /** Text produced before the failure, if any. */
  partial?: string;
  /**
   * The assistant message this turn persisted before it failed (ORA-459).
   * A turn that produced text writes that partial answer, so a failed turn is
   * not always a turn that left nothing behind: with the id the client can
   * retry or regenerate that message, and its absence is how the client knows
   * the conversation the turn created was thrown away with it.
   */
  messageId?: string | null;
  /** The conversation the partial answer was written to, when there is one. */
  conversationId?: string;
}

export interface ChatPingEvent extends ChatStreamEventEnvelope {
  type: 'ping';
}

export type ChatStreamEvent =
  | ChatTurnStartEvent
  | ChatStatusEvent
  | ChatToolCallEvent
  | ChatToolResultEvent
  | ChatBlockEvent
  | ChatTextDeltaEvent
  | ChatTextDoneEvent
  | ChatFollowupsEvent
  | ChatActionProposedEvent
  | ChatActionResolvedEvent
  | ChatTitleEvent
  | ChatTurnDoneEvent
  | ChatErrorEvent
  | ChatPingEvent;

/** `turn.done` and `error` end the stream. */
export const isTerminalChatStreamEvent = (event: ChatStreamEvent): boolean =>
  event.type === 'turn.done' || event.type === 'error';

/** An image attachment already uploaded to S3, referenced by key. */
export interface ChatTurnAttachment {
  key: string;
  mimeType: string;
  sizeBytes: number;
}

/**
 * The turn request body. The app-context envelope is assembled by the central
 * hook (ORA-449). The send path adds client platform metadata immediately
 * before this module serializes it, so this module does not need to know the
 * envelope's full shape.
 */
export interface ChatTurnRequest {
  message: string;
  /** Omitted when starting a new conversation; the server assigns one. */
  conversationId?: string;
  /** New Chat asks the backend to create a distinct thread for this anchor. */
  forceNewConversation?: boolean;
  /** Sanitized, size-capped app context. Opaque to the stream client. */
  envelope?: Record<string, unknown>;
  attachments?: ChatTurnAttachment[];
  /** Owner-scoped prepared document ids, additive to image attachments. */
  documentIds?: string[];
  /** Message id this turn regenerates (retry and regenerate share this path). */
  regenerateOf?: string;
  /**
   * Runs the loop on a higher tier with one extra step. Nothing in the app
   * sends it any more — ORA-463 replaced the "Go deeper" control with runtime
   * escalation — but the backend still accepts it, so the field stays on the
   * request type rather than being reintroduced by the next caller that needs
   * a deeper turn.
   */
  depth?: 'deep';
}
